import { useState, useEffect, useMemo } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import Breadcrumb from './Breadcrumb';
import { Container, Row, Col, Card, Image, Form, Button, Badge, Nav, Table, Pagination, Modal } from 'react-bootstrap';
import Select, { components } from 'react-select';
import icCoins from './assets/img/ic-coins.svg';
import icChartUp from './assets/img/ic-chart-up.svg';
import icChartDown from './assets/img/ic-chart-down.svg';
import icNoInsights from './assets/img/ic-empty-box.svg';
import defaultProfileImage from './assets/img/ic-img.svg';
import { exportReportingRevenuePdf } from './api/exportReportingRevenuePdf.js';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';

const ROLE_TO_USER_CLASS = {
    student: 'Talent',
    company: 'Company',
    admin: 'Abroadify',
    superadmin: 'Abroadify',
    titanAdmin: 'Abroadify',
    donatorUser: 'Partner',
};

const ROLE_TO_USER_TYPE = {
    student: 'Student',
    company: 'Company',
    admin: 'Admin',
    superadmin: 'Super Admin',
    titanAdmin: 'Titan Admin',
    donatorUser: 'Donator',
};

const formatShortDate = (isoDate) => {
    if (!isoDate) return '—';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatEur = (amountCents) => {
    return `${Math.round((amountCents || 0) / 100).toLocaleString()}€`;
};

const formatAmountPerYear = (amount, currency = 'CHF') => {
    return `${(amount || 0).toLocaleString()} ${currency}`;
};

// Subscriptions/packages aren't tracked yet - there is no plan or recurring billing model in the
// backend, so this side of the report has no data to show until that exists.
const EMPTY_SUBSCRIPTIONS_SUMMARY = {
    talentAmount: 0,
    companyAmount: 0,
    totalAmount: 0,
    countThisMonth: 0,
    countThisYear: 0,
    countThisMonthTrend: 0,
    countThisYearTrend: 0,
};

const mapDonationToRow = (payment) => {
    const u = payment.user || {};
    return {
        id: payment._id,
        createdAt: payment.createdAt,
        name: (u.firstName || u.familyName) ? `${u.firstName || ''} ${u.familyName || ''}`.trim() : (u.email || 'No name'),
        userClass: ROLE_TO_USER_CLASS[u.role] || u.role || '—',
        userType: ROLE_TO_USER_TYPE[u.role] || u.role || '—',
        company: u.company?.title || 'N/A',
        country: u.country || u.company?.country || '—',
        packageName: 'One-time donation',
        amount: Math.round((payment.amount || 0) / 100),
        currency: String(payment.currency || 'eur').toUpperCase(),
    };
};

const ReportingRevenueList = () => {
    const [tickets, setTickets] = useState([]);
    const [revenueStats, setRevenueStats] = useState(null);
    const [donationRows, setDonationRows] = useState([]);

    const [activeTab, setActiveTab] = useState('subscriptions');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedCompany, setSelectedCompany] = useState('');

    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
    const [filterSetName, setFilterSetName] = useState('');
    const [showDeleteFilterModal, setShowDeleteFilterModal] = useState(false);
    const [filterSetToDelete, setFilterSetToDelete] = useState(null);

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                const res = await api.get('/api/tickets');
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }
        };
        fetchTickets();
    }, []);

    useEffect(() => {
        const fetchRevenueStats = async () => {
            try {
                const res = await api.get('/api/reporting/revenue');
                setRevenueStats(res.data);
            } catch (error) {
                console.error('Error fetching revenue report:', error);
            }
        };
        fetchRevenueStats();
    }, []);

    useEffect(() => {
        const fetchDonations = async () => {
            try {
                const res = await api.get('/api/reporting/revenue/donations');
                setDonationRows((res.data || []).map(mapDonationToRow));
            } catch (error) {
                console.error('Error fetching revenue donations:', error);
            }
        };
        fetchDonations();
    }, []);

    useEffect(() => {
        const fetchFilterSets = async () => {
            try {
                const res = await api.get('/api/reporting-filter-sets?page=reporting-revenue');
                setSavedFilterSets(Array.isArray(res.data) ? res.data : []);
            } catch (error) {
                console.error('Error fetching reporting revenue filter sets:', error);
            }
        };
        fetchFilterSets();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchTerm, selectedUser, selectedCountry, selectedCompany]);

    const activeRows = useMemo(
        () => (activeTab === 'subscriptions' ? [] : donationRows),
        [activeTab, donationRows]
    );

    const uniqueUserNames = useMemo(() =>
        [...new Set(activeRows.map((r) => r.name).filter(Boolean))].sort(),
        [activeRows]
    );

    const uniqueCountries = useMemo(() =>
        [...new Set(activeRows.map((r) => r.country).filter(Boolean))].sort(),
        [activeRows]
    );

    const uniqueCompanies = useMemo(() =>
        [...new Set(activeRows.map((r) => r.company).filter((c) => c && c !== 'N/A'))].sort(),
        [activeRows]
    );

    const filteredRows = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();
        return activeRows.filter((row) => {
            if (search && !row.name.toLowerCase().includes(search)) return false;
            if (selectedUser && row.name !== selectedUser) return false;
            if (selectedCountry && row.country !== selectedCountry) return false;
            if (selectedCompany && row.company !== selectedCompany) return false;
            return true;
        });
    }, [activeRows, searchTerm, selectedUser, selectedCountry, selectedCompany]);

    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
    const currentRows = filteredRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
    const noResults = filteredRows.length === 0;

    const buildCurrentFiltersPayload = () => ({
        searchTerm,
        selectedUser,
        selectedCountry,
        selectedCompany,
    });

    const applySavedFilterSet = (filters = {}) => {
        setSearchTerm(filters.searchTerm || '');
        setSelectedUser(filters.selectedUser || '');
        setSelectedCountry(filters.selectedCountry || '');
        setSelectedCompany(filters.selectedCompany || '');
    };

    const handleSaveFilters = async (name) => {
        try {
            const trimmedName = String(name || '').trim();
            if (!trimmedName) return;

            const res = await api.post('/api/reporting-filter-sets', {
                page: 'reporting-revenue',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            });
            const saved = res.data;

            setSavedFilterSets((prev) => {
                const without = prev.filter((s) => s._id !== saved._id && s.name !== saved.name);
                return [saved, ...without];
            });
            setSelectedFilterSet(saved._id);
        } catch (error) {
            console.error('Error saving filter set:', error);
            alert(error?.response?.data?.msg || 'An error occurred while saving the filter set.');
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);
        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }
        const match = savedFilterSets.find((s) => s._id === value);
        if (match?.filters) applySavedFilterSet(match.filters);
    };

    const handleDeleteFilterSet = async (filterSetId) => {
        if (!filterSetId || filterSetId === 'default') return;
        try {
            await api.delete(`/api/reporting-filter-sets/${filterSetId}`);
            const wasSelected = String(selectedFilterSet) === String(filterSetId);
            setSavedFilterSets((prev) => prev.filter((s) => String(s._id) !== String(filterSetId)));
            if (wasSelected) {
                setSelectedFilterSet('default');
                applySavedFilterSet({});
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedUser('');
        setSelectedCountry('');
        setSelectedCompany('');
    };

    const handleExportPdf = async () => {
        try {
            await exportReportingRevenuePdf({ revenueStats, mockSubscriptionsSummary: EMPTY_SUBSCRIPTIONS_SUMMARY });
        } catch (error) {
            console.error('Error exporting reporting PDF:', error);
            alert('An error occurred while exporting the reporting PDF.');
        }
    };

    const totalRevenue = (revenueStats?.totalDonationsAmount || 0) + EMPTY_SUBSCRIPTIONS_SUMMARY.totalAmount;

    const paymentTypeRows = [
        { label: 'Talent Subscriptions', value: formatEur(EMPTY_SUBSCRIPTIONS_SUMMARY.talentAmount) },
        { label: 'Talent Donations', value: formatEur(revenueStats?.talentDonationsAmount) },
        { label: 'Company Subscriptions', value: formatEur(EMPTY_SUBSCRIPTIONS_SUMMARY.companyAmount) },
        { label: 'Company Donations', value: formatEur(revenueStats?.companyDonationsAmount) },
    ];

    const revenueMetricTiles = [
        {
            labelPrefix: 'Total donations this',
            labelBold: 'month',
            value: formatEur(revenueStats?.donationsThisMonth),
            trendDirection: (revenueStats?.donationsMonthTrend ?? 0) >= 0 ? 'up' : 'down',
            trendPercent: `${Math.abs(revenueStats?.donationsMonthTrend ?? 0)}%`,
            trendText: (revenueStats?.donationsMonthTrend ?? 0) >= 0 ? 'Up from last month' : 'Down from last month',
        },
        {
            labelPrefix: 'Total donations this',
            labelBold: 'year',
            value: formatEur(revenueStats?.donationsThisYear),
            trendDirection: (revenueStats?.donationsYearTrend ?? 0) >= 0 ? 'up' : 'down',
            trendPercent: `${Math.abs(revenueStats?.donationsYearTrend ?? 0)}%`,
            trendText: (revenueStats?.donationsYearTrend ?? 0) >= 0 ? 'Up from last year' : 'Down from last year',
        },
        {
            labelPrefix: 'Total subscriptions this',
            labelBold: 'month',
            value: EMPTY_SUBSCRIPTIONS_SUMMARY.countThisMonth.toLocaleString(),
            trendDirection: EMPTY_SUBSCRIPTIONS_SUMMARY.countThisMonthTrend >= 0 ? 'up' : 'down',
            trendPercent: `${Math.abs(EMPTY_SUBSCRIPTIONS_SUMMARY.countThisMonthTrend)}%`,
            trendText: EMPTY_SUBSCRIPTIONS_SUMMARY.countThisMonthTrend >= 0 ? 'Up from last month' : 'Down from last month',
        },
        {
            labelPrefix: 'Total subscriptions this',
            labelBold: 'year',
            value: EMPTY_SUBSCRIPTIONS_SUMMARY.countThisYear.toLocaleString(),
            trendDirection: EMPTY_SUBSCRIPTIONS_SUMMARY.countThisYearTrend >= 0 ? 'up' : 'down',
            trendPercent: `${Math.abs(EMPTY_SUBSCRIPTIONS_SUMMARY.countThisYearTrend)}%`,
            trendText: EMPTY_SUBSCRIPTIONS_SUMMARY.countThisYearTrend >= 0 ? 'Up from last year' : 'Down from last year',
        },
    ];

    const filterSetOptions = [
        { value: 'default', label: 'Default filter set', isDefault: true },
        ...savedFilterSets.map((fs) => ({
            _id: fs._id,
            value: fs._id,
            label: fs.name,
            name: fs.name,
            isDefault: false,
        })),
    ];
    const selectedFilterSetOption = filterSetOptions.find((o) => o.value === selectedFilterSet) || filterSetOptions[0];

    const FilterSetOption = (props) => (
        <components.Option {...props}>
            <div className="d-flex align-items-center justify-content-between w-100">
                <span>{props.data.label}</span>
                {!props.data.isDefault && (
                    <button
                        type="button"
                        className="filter-set-delete-btn"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setFilterSetToDelete(props.data);
                            setShowDeleteFilterModal(true);
                        }}
                        aria-label={`Delete ${props.data.label} filter set`}
                    >
                        ×
                    </button>
                )}
            </div>
        </components.Option>
    );

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body>
                                    <Row>
                                        <Col xs={12}>
                                            <h1 className="mb-4">Revenue</h1>
                                        </Col>
                                    </Row>

                                    <div className="section-box mb-1">
                                        <Row className="mb-4">
                                            <Col md={5}>
                                                <div className="stat-box">
                                                    <div className="stat-box-icon">
                                                        <Image src={icCoins} alt="Total Revenue" />
                                                    </div>
                                                    <span className="stat-box-label mb-3">Total Revenue</span>
                                                    <span className="stat-box-value">{formatEur(totalRevenue)}</span>
                                                </div>
                                            </Col>
                                            <Col md={7}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">Payment type</div>
                                                    {paymentTypeRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{row.value}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <Row>
                                            {revenueMetricTiles.map((tile) => (
                                                <Col md={3} key={`${tile.labelPrefix}-${tile.labelBold}`}>
                                                    <div className="mini-stat-box">
                                                        <div className="mini-stat-box-label mb-3">
                                                            {tile.labelPrefix} <strong>{tile.labelBold}</strong>
                                                        </div>
                                                        <div className="mini-stat-box-value">{tile.value}</div>
                                                        <div className={`mini-stat-box-trend ${tile.trendDirection}`}>
                                                            <Image src={tile.trendDirection === 'up' ? icChartUp : icChartDown} alt="" />
                                                            <span className="mini-stat-box-trend-percent">{tile.trendPercent}</span>
                                                            <span>{tile.trendText}</span>
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>

                                    <div className="pt-5 mb-5">
                                        <Row className="mb-4">
                                            <Col>
                                                <h3 className="mb-0">List of Payments</h3>
                                            </Col>
                                        </Row>

                                        <Row className="mb-4 pt-1">
                                            <Col>
                                                <Nav variant="pills" className="nav-pills-view-toggle">
                                                    <Nav.Item>
                                                        <Nav.Link active={activeTab === 'subscriptions'} onClick={() => setActiveTab('subscriptions')}>
                                                            Subscriptions
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                    <Nav.Item>
                                                        <Nav.Link active={activeTab === 'donations'} onClick={() => setActiveTab('donations')}>
                                                            Donations
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                </Nav>
                                            </Col>
                                        </Row>

                                        {activeRows.length > 0 && (
                                            <div className="filter discovery-filter-bar">
                                                <div className="d-block">
                                                    <Row className="mb-3 align-items-center">
                                                        <Col xs={12} md>
                                                            <h3 className="mb-0">{activeTab === 'subscriptions' ? 'Subscriptions' : 'Donations'}</h3>
                                                        </Col>
                                                        <Col xs="auto">
                                                            <div style={{ minWidth: '160px', width: 'auto' }}>
                                                                <Select
                                                                    options={filterSetOptions}
                                                                    value={selectedFilterSetOption}
                                                                    onChange={(opt) => handleFilterSetChange(opt?.value || 'default')}
                                                                    isSearchable={false}
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                    className="react-select sm"
                                                                    classNamePrefix="react-select"
                                                                    components={{ Option: FilterSetOption }}
                                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <Row className="g-3 align-items-end flex-nowrap">
                                                        <Col md={6} lg>
                                                            <Form.Group>
                                                                <Form.Label>Search</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder="Search..."
                                                                    value={searchTerm}
                                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                                />
                                                            </Form.Group>
                                                        </Col>

                                                        <Col md={6} lg>
                                                            <Form.Group>
                                                                <Form.Label>User</Form.Label>
                                                                <Form.Select
                                                                    value={selectedUser}
                                                                    onChange={(e) => setSelectedUser(e.target.value)}
                                                                >
                                                                    <option value="">Please select</option>
                                                                    {uniqueUserNames.map((name) => (
                                                                        <option key={name} value={name}>{name}</option>
                                                                    ))}
                                                                </Form.Select>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col md={6} lg>
                                                            <Form.Group>
                                                                <Form.Label>Country</Form.Label>
                                                                <Form.Select
                                                                    value={selectedCountry}
                                                                    onChange={(e) => setSelectedCountry(e.target.value)}
                                                                >
                                                                    <option value="">Please select</option>
                                                                    {uniqueCountries.map((country) => (
                                                                        <option key={country} value={country}>{country}</option>
                                                                    ))}
                                                                </Form.Select>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col md={6} lg>
                                                            <Form.Group>
                                                                <Form.Label>Company</Form.Label>
                                                                <Form.Select
                                                                    value={selectedCompany}
                                                                    onChange={(e) => setSelectedCompany(e.target.value)}
                                                                >
                                                                    <option value="">Please select</option>
                                                                    {uniqueCompanies.map((company) => (
                                                                        <option key={company} value={company}>{company}</option>
                                                                    ))}
                                                                </Form.Select>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col xs="auto" className="d-flex align-items-end">
                                                            <Button
                                                                variant="primary"
                                                                className="btn-sm discovery-filter-save-btn"
                                                                onClick={() => setShowSaveFilterModal(true)}
                                                            >
                                                                Save filters
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                {(searchTerm || selectedUser || selectedCountry || selectedCompany) && (
                                                    <Row className="filter-results">
                                                        <Col className="d-flex align-items-center flex-wrap gap-2">
                                                            <span>Showing results:</span>

                                                            {searchTerm && (
                                                                <Badge bg="info">
                                                                    Search: {searchTerm}{' '}
                                                                    <Button variant="link" size="sm" onClick={() => setSearchTerm('')}>×</Button>
                                                                </Badge>
                                                            )}

                                                            {selectedUser && (
                                                                <Badge bg="info">
                                                                    User: {selectedUser}{' '}
                                                                    <Button variant="link" size="sm" onClick={() => setSelectedUser('')}>×</Button>
                                                                </Badge>
                                                            )}

                                                            {selectedCountry && (
                                                                <Badge bg="info">
                                                                    Country: {selectedCountry}{' '}
                                                                    <Button variant="link" size="sm" onClick={() => setSelectedCountry('')}>×</Button>
                                                                </Badge>
                                                            )}

                                                            {selectedCompany && (
                                                                <Badge bg="info">
                                                                    Company: {selectedCompany}{' '}
                                                                    <Button variant="link" size="sm" onClick={() => setSelectedCompany('')}>×</Button>
                                                                </Badge>
                                                            )}

                                                            <Button variant="link" size="sm" className="ms-auto" onClick={clearFilters}>
                                                                Clear all
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </div>
                                        )}

                                        {noResults ? (
                                            <Row>
                                                <Col md={12}>
                                                    <Row className="mb-3">
                                                        <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                                            <Image src={icNoInsights} alt="No results" className="mb-4" />
                                                            <h2 className="mb-3">No {activeTab} yet.</h2>
                                                            <p className="mb-4">Your {activeTab} will be displayed here.</p>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        ) : (
                                            <>
                                                <Row>
                                                    <Col>
                                                        <div className="table-responsive list">
                                                            <Table>
                                                                <thead>
                                                                    <tr>
                                                                        <th><div>Date</div></th>
                                                                        <th className="th-fixed-company"><div>Name</div></th>
                                                                        <th><div>User class</div></th>
                                                                        <th><div>User types</div></th>
                                                                        <th><div>Company</div></th>
                                                                        <th><div>Country</div></th>
                                                                        <th><div>Package name</div></th>
                                                                        <th><div>Amount per year</div></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {currentRows.map((row) => (
                                                                        <tr key={row.id}>
                                                                            <td>{formatShortDate(row.createdAt)}</td>
                                                                            <td>
                                                                                <div>
                                                                                    <img
                                                                                        src={defaultProfileImage}
                                                                                        alt={row.name}
                                                                                        className="round"
                                                                                    />
                                                                                    <strong>{row.name}</strong>
                                                                                </div>
                                                                            </td>
                                                                            <td>{row.userClass}</td>
                                                                            <td>{row.userType}</td>
                                                                            <td>{row.company}</td>
                                                                            <td>{row.country}</td>
                                                                            <td>{row.packageName}</td>
                                                                            <td>{formatAmountPerYear(row.amount, row.currency)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row className="pagination-wrapper">
                                                    <Col className="d-flex align-items-center">
                                                        <span>
                                                            Total {filteredRows.length} {filteredRows.length === 1 ? 'user' : 'users'}
                                                        </span>
                                                        <Pagination>
                                                            <Pagination.Prev
                                                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                                                disabled={currentPage === 1}
                                                            >
                                                                <BsChevronLeft />
                                                            </Pagination.Prev>
                                                            {[...Array(totalPages).keys()].map((n) => (
                                                                <Pagination.Item
                                                                    key={n + 1}
                                                                    active={n + 1 === currentPage}
                                                                    onClick={() => setCurrentPage(n + 1)}
                                                                >
                                                                    {n + 1}
                                                                </Pagination.Item>
                                                            ))}
                                                            <Pagination.Next
                                                                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                                                disabled={currentPage === totalPages || totalPages === 0}
                                                            >
                                                                <BsChevronRight />
                                                            </Pagination.Next>
                                                        </Pagination>
                                                    </Col>
                                                    <Col className="pagination-controls">
                                                        <div className="pagination-controls-per-page">
                                                            <Form.Select
                                                                value={rowsPerPage}
                                                                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                                            >
                                                                <option value="10">10 / page</option>
                                                                <option value="20">20 / page</option>
                                                                <option value="40">40 / page</option>
                                                            </Form.Select>
                                                        </div>
                                                        <label className="pagination-controls-goto-label" htmlFor="reporting-revenue-goto">
                                                            Go to
                                                        </label>
                                                        <Form.Control
                                                            id="reporting-revenue-goto"
                                                            className="pagination-controls-goto-input"
                                                            type="number"
                                                            value={currentPage}
                                                            onChange={(e) => {
                                                                const p = Number(e.target.value);
                                                                if (p >= 1 && p <= totalPages) setCurrentPage(p);
                                                            }}
                                                            onBlur={() => {
                                                                if (currentPage < 1) setCurrentPage(1);
                                                                if (currentPage > totalPages) setCurrentPage(totalPages);
                                                            }}
                                                        />
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal
                className="with-form"
                show={showSaveFilterModal}
                onHide={() => { setShowSaveFilterModal(false); setFilterSetName(''); }}
                centered
            >
                <Form onSubmit={async (e) => {
                    e.preventDefault();
                    const trimmed = filterSetName.trim();
                    if (!trimmed) return;
                    await handleSaveFilters(trimmed);
                    setShowSaveFilterModal(false);
                    setFilterSetName('');
                }}>
                    <Modal.Header closeButton>
                        <Modal.Title>Save filter set as</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>This will create a new set of filters on the Revenue reporting page.</p>
                        <Form.Group className="mb-0">
                            <Form.Label>Filter set name</Form.Label>
                            <Form.Control
                                type="text"
                                value={filterSetName}
                                onChange={(e) => setFilterSetName(e.target.value)}
                                placeholder="Enter filter set name"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => { setShowSaveFilterModal(false); setFilterSetName(''); }}>
                            Cancel
                        </Button>
                        <Button variant="primary" className="ms-3" type="submit" disabled={!filterSetName.trim()}>
                            Save
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal
                className="with-form"
                show={showDeleteFilterModal}
                onHide={() => { setShowDeleteFilterModal(false); setFilterSetToDelete(null); }}
                centered
            >
                <Form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!filterSetToDelete?._id && !filterSetToDelete?.value) return;
                    await handleDeleteFilterSet(filterSetToDelete._id || filterSetToDelete.value);
                    setShowDeleteFilterModal(false);
                    setFilterSetToDelete(null);
                }}>
                    <Modal.Header closeButton>
                        <Modal.Title>Are you sure you want to delete "{filterSetToDelete?.name || filterSetToDelete?.label}"?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Deleting this filter set will remove all its saved criteria.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => { setShowDeleteFilterModal(false); setFilterSetToDelete(null); }}>
                            No, keep this filter set
                        </Button>
                        <Button variant="primary" className="ms-3" type="submit">
                            Yes, delete this filter set
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default ReportingRevenueList;
